import { intl } from '@approvalmax/utils';
import { selectors } from 'modules/common';
import { domain } from 'modules/data';
import { useCurrentUser } from 'modules/utils';
import moment from 'moment';
import { useMemo } from 'react';

import { getShowRequestAmount } from '../../selectors/requestSelectors';
import { messages } from './RequestListItem.messages';

export const useAmount = (request: selectors.types.ExpandedRequest) => {
    if (!getShowRequestAmount(request)) {
        return null;
    }

    let text = intl.formatCurrency(request.amount, request.currency);

    if (request.integrationType === domain.IntegrationType.NetSuite && 'currency' in request.details) {
        const details = request.details;

        if (details.currency?.text) {
            text = intl.formatCurrency(request.amount, details.currency?.text, 2, false);
        }
    }

    if (request.integrationType === domain.IntegrationType.None) {
        text = intl.formatCurrency(request.amount, request.currency, 'auto');
    }

    const title = request.exchangeRate
        ? messages.exchangeRateTitle({
              amount: intl.formatNumber(request.amount),
              currency: request.currency,
              baseAmount: intl.formatNumber(request.amount / request.exchangeRate),
              baseCurrency: request.company.defaultCurrency,
          })
        : text;

    return {
        title,
        text,
    };
};

export const useWatchers = (request: selectors.types.ExpandedRequest) => {
    const me = useCurrentUser();

    const isWatchersAvailable = useMemo(
        () =>
            !request.company.betaFeatures.includes(domain.CompanyBetaFeature.HideButtonsExternalApproversV1) &&
            request.statusV2 !== domain.RequestStatusV2.Draft,
        [request.company.betaFeatures, request.statusV2]
    );

    const showIcon = useMemo(
        () => isWatchersAvailable && !!request.watchers.find((requestWatcher) => requestWatcher.watcher.id === me.id),
        [isWatchersAvailable, me.id, request.watchers]
    );

    return {
        showIcon,
    };
};

export const useScheduledPaymentLabel = (request: selectors.types.ExpandedRequest) => {
    if (request.integrationCode === domain.IntegrationCode.XeroAirwallexBatchPayment) {
        const { scheduledPaymentFailed, scheduledPaymentDate } = request.details;

        if (!scheduledPaymentDate) {
            return null;
        }

        const momentDate = moment(scheduledPaymentDate);
        const dateString = momentDate.local().format('DD MMM YYYY');

        return { failed: scheduledPaymentFailed, dateString };
    }

    return null;
};
